import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, FileText, Headphones, LinkIcon, PlayCircle } from 'lucide-react';
import { api } from '../lib/api';
import { PatientHeader } from './PatientLayout';
import { Card, EmptyState, ErrorState, Spinner, cx } from '../components/ui';

export interface ContentSummary {
  id: string;
  slug: string;
  type: 'ARTICLE' | 'VIDEO' | 'AUDIO' | 'PDF' | 'LINK';
  title: string;
  summary: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  tags: string[];
  category: { name: string; slug: string } | null;
  saved: boolean;
}

interface ContentListResponse {
  categories: Array<{ id: string; name: string; slug: string; icon: string }>;
  items: ContentSummary[];
}

export const CONTENT_ICONS = {
  ARTICLE: FileText,
  VIDEO: PlayCircle,
  AUDIO: Headphones,
  PDF: FileText,
  LINK: LinkIcon,
} as const;

export function ContentListPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const category = params.get('categoria');

  const content = useQuery({
    queryKey: ['me', 'content', category],
    queryFn: () =>
      api.get<ContentListResponse>(`/me/content${category ? `?category=${category}` : ''}`),
  });

  if (content.isError) return <ErrorState error={content.error} onRetry={() => content.refetch()} />;
  if (content.isLoading || !content.data) return <Spinner />;

  const data = content.data!;
  const activeCategory = data.categories.find((entry) => entry.slug === category);

  return (
    <div className="max-w-lg lg:max-w-3xl mx-auto">
      <PatientHeader
        title={activeCategory?.name ?? 'Contenidos'}
        subtitle={
          activeCategory ? undefined : 'Información revisada por tu equipo médico'
        }
      />

      <div className="px-5 py-5 space-y-5">
        {/* Los filtros bajan de renglón en vez de irse de costado.
            Con scroll lateral, las categorías que quedan fuera de pantalla no
            se ven ni se intuyen: el paciente cree que el programa tiene tres
            temas cuando en realidad tiene siete. Ocupar dos renglones es un
            costo mucho menor que esconder contenido. */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setParams({})}
            className={cx(
              'px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              !category
                ? 'bg-primary text-primary-fg'
                : 'bg-surface-raised border border-border text-fg-muted',
            )}
          >
            Todo
          </button>
          {data.categories.map((entry) => (
            <button
              key={entry.slug}
              onClick={() => setParams({ categoria: entry.slug })}
              className={cx(
                'px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                category === entry.slug
                  ? 'bg-primary text-primary-fg'
                  : 'bg-surface-raised border border-border text-fg-muted',
              )}
            >
              {entry.name}
            </button>
          ))}
        </div>

        {data.items.length === 0 ? (
          <EmptyState
            icon={<BookOpen className="size-8" />}
            title="Todavía no hay contenidos acá"
            description="Tu equipo va a ir publicando material a lo largo del acompañamiento."
          />
        ) : (
          // En escritorio la biblioteca va en dos columnas: una lista de tarjetas
          // anchas y bajas obliga a barrer la pantalla de lado a lado para leer
          // cada título.
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-3">
            {data.items.map((item) => (
              <ContentRow key={item.id} item={item} to={`/${slug}/app/contenidos/${item.slug}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ContentRow({ item, to }: { item: ContentSummary; to: string }) {
  const TypeIcon = CONTENT_ICONS[item.type] ?? FileText;

  return (
    <Link to={to} className="block">
      <Card className="p-4 flex gap-3 hover:border-primary transition-colors">
        <div className="size-10 rounded-md bg-primary-soft text-primary grid place-items-center shrink-0">
          <TypeIcon className="size-5" />
        </div>

        <div className="min-w-0 grow">
          <p className="font-semibold text-[15px] leading-snug">{item.title}</p>
          {item.summary && (
            <p className="text-sm text-fg-muted mt-1 leading-snug line-clamp-2">{item.summary}</p>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-fg-subtle">
            {item.category && <span>{item.category.name}</span>}
            {item.durationSec && (
              <>
                <span aria-hidden>·</span>
                <span>{Math.round(item.durationSec / 60)} min</span>
              </>
            )}
            {item.saved && (
              <>
                <span aria-hidden>·</span>
                <span className="text-primary font-medium">Guardado</span>
              </>
            )}
          </div>
        </div>

        <ChevronRight className="size-4 text-fg-subtle shrink-0 self-center" />
      </Card>
    </Link>
  );
}
